<script setup lang="ts">
import { groupBy } from 'lodash';
import { useFieldArray } from 'vee-validate';
import { computed, toRef } from 'vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { I18nKey } from '@/utils/typings';
import { ScopeType, getTranslationPrefix } from '@/utils/translationPrefix';
import { useTranslation } from 'i18next-vue';
import { Scope, ScopeKey } from '@/views/enumerations/types';
import { localizedEnumCompare, withLastItemCompare } from '@/utils/compareAndSort';
import ScopeList from './ScopeList.vue';

const { t } = useTranslation();

const mode = useFormMode();

export interface ScopeGroup {
  title: string;
  options: Scope[];
}

const props = withDefaults(
  defineProps<{
    name: string;
    disabled?: boolean;
    disableSettedOptions?: boolean;
    scopeOptions: Scope[];
    scopeType?: ScopeType;
    scopeKey: ScopeKey;
    groupKey: ScopeKey;
  }>(),
  {
    scopeType: 'medicalSystemScope',
    disableSettedOptions: false,
  },
);

const scopes = useFieldArray<Scope>(toRef(() => props.name));
const options = computed(() => groupScopes(props.scopeOptions, props.scopeType, props.groupKey));

function groupScopes(
  scopesToGroup: Scope[],
  scopeType: ScopeType,
  key: ScopeKey = 'rtSystemSubCategory',
): ScopeGroup[] {
  const groups = groupBy<Scope>(scopesToGroup, key);

  const enumPrefix = getTranslationPrefix(scopeType, props.groupKey);

  function isLastItem(scope: Scope): boolean {
    return Boolean(scope[props.scopeKey]?.startsWith('Other'));
  }

  return Object.keys(groups).map((groupKey) => ({
    title: t(`${enumPrefix}.${groupKey}` as I18nKey),
    options: groups[groupKey].toSorted(
      withLastItemCompare(
        (x: Scope, y: Scope) => {
          const a = x[props.scopeKey] || undefined;
          const b = y[props.scopeKey] || undefined;

          if (enumPrefix && a && b) {
            return localizedEnumCompare(enumPrefix)(a, b);
          } else {
            return 0;
          }
        },
        (scope: Scope) => isLastItem(scope),
      ),
    ),
  }));
}

const selectedEntries = computed(() =>
  options.value.flatMap((group) =>
    group.options.filter((option) =>
      scopes.fields.value.some((scope) => scope.value[props.scopeKey] === option[props.scopeKey]),
    ),
  ),
);
</script>

<template>
  <p
    v-if="selectedEntries.length === 0 && mode === 'details'"
    class="text-body-1 font-weight-semibold text-primary-lighten-2 mt-3"
  >
    {{ $t('NoCategoriesSelectedMsg') }}
  </p>

  <div v-else class="d-flex">
    <div
      v-for="(scopeGroup, outerIndex) in options"
      :key="scopeGroup.title"
      class="mt-4"
      :class="{ 'mr-8': outerIndex < options.length - 1 }"
    >
      <v-label
        class="text-body-2 mb-2"
        :class="[!disabled ? 'text-primary' : 'text-primary-lighten-2']"
      >
        {{ scopeGroup.title }}
      </v-label>
      <ScopeList
        :name="name"
        :scopeOptions="scopeGroup.options"
        :scopeKey="scopeKey"
        :disabled="disabled"
        :disableSettedOptions="disableSettedOptions"
        :scopeType="scopeType"
        :lastItemKey="
          scopeGroup.options[scopeGroup.options.length - 1][props.scopeKey] || undefined
        "
      ></ScopeList>
    </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-list-item) {
  min-height: 36px;

  .v-list-item__overlay {
    background: unset;
  }

  &:hover {
    background: rgb(var(--v-theme-secondary-lighten-5));
  }

  .icon {
    margin-right: 8px;
  }
}

.group {
  min-width: 170px;
}
</style>
